import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import StarterContainer from "@/components/StarterContainer";
import { Button } from "@/components/ui/button";
import { confirmEmail, resendConfirmationEmail } from "@/services/shared/auth/auth.service";

type Status = "idle" | "loading" | "success" | "error";

export const ConfirmEmailPage = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string>("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!userId || !token) {
      setStatus("error");
      setMessage("Link inválido ou incompleto.");
      return;
    }

    let cancelled = false;
    setStatus("loading");

    void (async () => {
      try {
        await confirmEmail(userId, token);
        if (!cancelled) {
          setStatus("success");
          setMessage("Email confirmado! Agora você pode fazer login.");
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
          setMessage("Link inválido ou expirado.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, token]);

  const handleResend = async () => {
    if (!userId) return;
    setResendMessage(null);
    setResendLoading(true);
    try {
      await resendConfirmationEmail({ userId });
      setResendMessage({ type: "success", text: "Email reenviado. Verifique sua caixa de entrada." });
    } catch (e) {
      setResendMessage({ type: "error", text: e instanceof Error ? e.message : "Falha ao reenviar email." });
    } finally {
      setResendLoading(false);
    }
  };

  const isPending = status === "idle" || status === "loading";

  return (
    <StarterContainer>
      <h2 className="text-2xl font-bold">Confirmação de email</h2>

      {isPending && (
        <p className="mt-4 text-muted-foreground">Confirmando seu email...</p>
      )}

      {status === "success" && (
        <>
          <p className="mt-4 text-green-600 dark:text-green-400">{message}</p>
          <Button asChild className="mt-6 w-full">
            <Link to="/login">Ir para o login</Link>
          </Button>
        </>
      )}

      {status === "error" && (
        <>
          <p className="mt-4 text-destructive">{message}</p>
          {resendMessage && (
            <p className={`mt-2 ${resendMessage.type === "success" ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
              {resendMessage.text}
            </p>
          )}
          {userId && (
            <Button
              type="button"
              variant="secondary"
              className="mt-4 w-full"
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading ? "Enviando..." : "Reenviar email"}
            </Button>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <Button asChild variant="default" className="w-full">
              <Link to="/login">Ir para o login</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/signup">Criar conta</Link>
            </Button>
          </div>
        </>
      )}
    </StarterContainer>
  );
};
